/**
 * API functions for managing class registration transfers
 */

import { api } from "./api-client";

// =============================================================================
// TYPES
// =============================================================================

/**
 * Transfer status type
 */
export type TransferStatus =
  | "pending"
  | "accepted"
  | "declined"
  | "withdrawn"
  | "expired"
  | "voided";

/**
 * Base transfer record
 */
export interface Transfer {
  id: string;
  class_id: string;
  registration_id: string;
  transferer_profile_id: string;
  transferee_profile_id: string;
  status: TransferStatus;
  payment_method: string | null;
  payment_amount: number | null;
  is_paid: boolean | null;
  paid_at: string | null;
  created_at: string;
  updated_at: string;
  responded_at: string | null;
}

/**
 * Profile info for transfer display
 */
export interface TransferProfile {
  id: string;
  full_name: string;
  username: string;
  profile_photo: string | null;
}

/**
 * Class info for transfer display
 */
export interface TransferClassInfo {
  id: string;
  title: string;
  date: string;
  start_time: string;
  end_time: string;
  timezone: string;
  location: string;
  status: string;
  profile_id: string;
  cover_photo_url: string | null;
  price: number | null;
}

/**
 * Transfer with full details for display
 */
export interface TransferWithDetails extends Transfer {
  transferer: TransferProfile;
  transferee: TransferProfile;
  class: TransferClassInfo;
}

// =============================================================================
// API RESPONSE TYPES
// =============================================================================

interface CreateTransferResponse {
  transfer: TransferWithDetails;
}

interface GetTransfersResponse {
  transfers: TransferWithDetails[];
}

interface RespondToTransferResponse {
  success: boolean;
  status: string;
  message: string;
}

interface WithdrawTransferResponse {
  transfer: Transfer;
}

// =============================================================================
// API FUNCTIONS
// =============================================================================

/**
 * Create a transfer invitation for a registration
 * @param registrationId The registration to transfer
 * @param transfereeProfileId The profile ID of the user to transfer to
 * @returns The created transfer with details
 */
export async function createTransfer(
  registrationId: string,
  transfereeProfileId: string
): Promise<TransferWithDetails> {
  const response = await api.post<CreateTransferResponse>("/transfers", {
    registration_id: registrationId,
    transferee_profile_id: transfereeProfileId,
  });
  return response.transfer;
}

/**
 * Get pending transfer invitations received by the current user
 * @returns Array of pending incoming transfers
 */
export async function getPendingIncomingTransfers(): Promise<TransferWithDetails[]> {
  const response = await api.get<GetTransfersResponse>(
    "/transfers/pending/incoming"
  );
  return response.transfers;
}

/**
 * Get pending transfer invitations sent by the current user
 * @returns Array of pending outgoing transfers
 */
export async function getPendingOutgoingTransfers(): Promise<TransferWithDetails[]> {
  const response = await api.get<GetTransfersResponse>(
    "/transfers/pending/outgoing"
  );
  return response.transfers;
}

/**
 * Respond to a transfer invitation (accept or decline)
 * @param transferId The transfer ID
 * @param action Accept or decline the transfer
 * @returns Success response with status
 */
export async function respondToTransfer(
  transferId: string,
  action: "accept" | "decline"
): Promise<RespondToTransferResponse> {
  const response = await api.put<RespondToTransferResponse>(
    `/transfers/${transferId}/respond`,
    { accept: action === "accept" }
  );
  return response;
}

/**
 * Withdraw a pending transfer invitation
 * @param transferId The transfer ID to withdraw
 * @returns The updated transfer
 */
export async function withdrawTransfer(transferId: string): Promise<Transfer> {
  const response = await api.put<WithdrawTransferResponse>(
    `/transfers/${transferId}/withdraw`
  );
  return response.transfer;
}
